import {
  Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { RemindersService } from '../../reminders/services/reminders.service';
import { AddReminderModalComponent } from '../../reminders/components/add-reminder-modal/add-reminder-modal.component';
import { ToasterService } from '../../../../services/core/toaster.service';
import { Observable, Subject, Subscription, of } from 'rxjs';
import {
  catchError, debounceTime, filter, finalize, map, switchMap, takeUntil, tap
} from 'rxjs/operators';
import {
  GLOBAL_SEARCH_MAX_LENGTH,
  GLOBAL_SEARCH_MIN_LENGTH,
  GlobalSearchService
} from '../../../../services/global-search.service';
import {
  GlobalSearchCategoryFilter,
  GlobalSearchItem,
  GlobalSearchModuleResult
} from '../../interfaces/global-search.interface';
import { globalSearchAnimations } from './global-search-animations';

/** Results fetched per page when scrolling through a single module in the dropdown. */
const MODULE_PAGE_SIZE = 20;
/** Load the next page when the list is scrolled within this many px of the bottom. */
const LOAD_MORE_THRESHOLD_PX = 80;

/**
 * Global Search Component
 * Always-visible search input in the admin header with grouped dropdown results.
 * Supports Ctrl+K / Cmd+K focus, keyboard navigation, category filters, and recent searches.
 */
@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    animations: globalSearchAnimations,
    standalone: false
})
export class GlobalSearchComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('');
  results: GlobalSearchModuleResult[] = [];
  filteredResults: GlobalSearchModuleResult[] = [];
  recentSearches: string[] = [];
  isLoading = false;
  hasSearched = false;
  showOverlay = false;
  /** Timestamp the overlay last opened at — see onBackdropClick(). */
  private overlayOpenedAt = 0;

  /** Set when the last search failed (Elasticsearch down → 503, validation → 422, network). */
  errorMessage: string | null = null;

  /** Exposed to the template (input maxlength) — mirrors the backend's max:100 rule. */
  readonly maxQueryLength = GLOBAL_SEARCH_MAX_LENGTH;

  /** Category filter chips */
  categoryFilters: GlobalSearchCategoryFilter[] = [];
  activeFilter = 'all';
  totalResultCount = 0;

  /** Index into flattened results for keyboard navigation */
  selectedIndex = -1;

  /** True while the next page of the module being scrolled through is loading. */
  isLoadingMore = false;

  /** Pages already loaded per module for the in-dropdown "View all" / single-category list. */
  private modulePaging = new Map<string, { page: number; hasMore: boolean }>();
  private moreSub?: Subscription;

  /** Detect macOS for keyboard shortcut hint */
  readonly isMac = typeof navigator !== 'undefined' && navigator.platform?.toLowerCase().includes('mac');

  private destroy$ = new Subject<void>();

  constructor(
    public globalSearchService: GlobalSearchService,
    private router: Router,
    private elementRef: ElementRef,
    private dialog: MatDialog,
    private remindersService: RemindersService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.recentSearches = this.globalSearchService.getRecentSearches();
    this.setupSearchPipe();
  }

  ngOnDestroy(): void {
    this.moreSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Current search term (used by highlight()) */
  get currentSearchTerm(): string {
    return this.searchControl.value || '';
  }

  /** Check if any results exist */
  get hasResults(): boolean {
    return this.results.some(r => r.items.length > 0);
  }

  /** Reactive search pipe — debounced, distinct, switched */
  private setupSearchPipe(): void {
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      map(q => this.globalSearchService.normalizeQuery(typeof q === 'string' ? q : '')),
      filter(q => q.length >= GLOBAL_SEARCH_MIN_LENGTH),
      debounceTime(300),
      // Skip only a query whose results are already on screen. This replaces
      // distinctUntilChanged(), which kept its own memory of the last query: clearing the
      // box silently (setValue('', {emitEvent:false}) after opening a result) never
      // reached it, so re-picking that same query from Recent Searches was dropped as a
      // duplicate and no search ran. latestQuery is reset whenever results are cleared.
      filter(q => q !== this.latestQuery),
      tap(q => {
        this.latestQuery = q;
        this.isLoading = true;
        this.selectedIndex = -1;
        this.errorMessage = null;
        this.resetModulePaging();
      }),
      switchMap(q => this.runSearch(q))
    ).subscribe(outcome => {
      // Ignore a response for a query the user has already replaced or cleared.
      if (outcome.query !== this.globalSearchService.normalizeQuery(this.searchControl.value)) {
        return;
      }
      this.showOverlay = true;
      if (outcome.error) {
        this.results = [];
        this.filteredResults = [];
        this.hasSearched = false;
        this.categoryFilters = [];
        this.totalResultCount = 0;
        this.errorMessage = outcome.error;
        return;
      }
      this.results = outcome.results ?? [];
      this.hasSearched = true;
      this.buildCategoryFilters();
      this.applyFilter();
      // A category stayed selected from the previous query: page through it for the new one.
      if (this.activeFilter !== 'all' && this.results.some(g => g.module === this.activeFilter)) {
        this.loadModulePage(this.activeFilter, 1);
      }
    });

    // Reset results when query is cleared or too short
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      map(q => this.globalSearchService.normalizeQuery(typeof q === 'string' ? q : '')),
      filter(q => q.length < GLOBAL_SEARCH_MIN_LENGTH)
    ).subscribe(() => {
      this.latestQuery = '';
      this.resetModulePaging();
      this.isLoading = false;
      this.errorMessage = null;
      this.results = [];
      this.filteredResults = [];
      this.hasSearched = false;
      this.selectedIndex = -1;
      this.categoryFilters = [];
      this.activeFilter = 'all';
      this.totalResultCount = 0;
    });
  }

  /** Run one preview search; a failure becomes a user-facing message instead of killing the stream. */
  private runSearch(q: string): Observable<{ query: string; results?: GlobalSearchModuleResult[]; error?: string }> {
    return this.globalSearchService.search(q).pipe(
      map(results => ({ query: q, results })),
      catchError(err => of({ query: q, error: this.describeError(err) })),
      // Only the newest request may switch the spinner off (an older, cancelled one must not).
      finalize(() => { if (this.latestQuery === q) { this.isLoading = false; } })
    );
  }

  /**
   * User-facing copy for a failed search request. Deliberately never surfaces the
   * backend's own error text (a raw exception/crash message, a validation string,
   * etc.) — whatever the cause, the dropdown just invites the user to search again
   * rather than showing something that reads like a backend/server error.
   */
  private describeError(_err: any): string {
    return 'Please try searching again, or try a different search term.';
  }

  /** The last query a request was started for (used to discard stale responses). */
  private latestQuery = '';

  /**
   * HTML-safe highlight for result text (title, identifier, description).
   * Delegates to GlobalSearchService.highlightText() — the one shared
   * implementation also used by GlobalSearchResultsComponent's "View all"
   * page — passing this component's own live search term.
   */
  highlight(text: string | null | undefined): string {
    return this.globalSearchService.highlightText(text, this.currentSearchTerm);
  }

  /**
   * Two-letter initials for the assignee avatar badge — same algorithm as
   * AvatarGroupComponent.getInitials() (shared/avatar-group/avatar-group.component.ts),
   * replicated here rather than importing that full component for one static badge.
   */
  getInitials(name: string | null | undefined): string {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * The stacked-avatar badges for one result item's real team (Sales Rep/
   * Estimator/Project Manager/Assigned To/etc., per module) — delegates to
   * GlobalSearchService.getTeamAvatars(), the one shared implementation
   * also used by GlobalSearchResultsComponent's "View all" page.
   */
  teamAvatars(item: GlobalSearchItem): Array<{ name: string; roles: string[]; initials: string; tooltip: string; color: string; statusSvg: SafeHtml | undefined; profileImageUrl: string }> {
    return this.globalSearchService.getTeamAvatars(item);
  }

  /** Build category filter chips from current results (counts are the backend's real totals) */
  private buildCategoryFilters(): void {
    this.totalResultCount = this.results.reduce((sum, g) => sum + g.total, 0);

    const filters: GlobalSearchCategoryFilter[] = [
      { key: 'all', label: 'All', icon: '', count: this.totalResultCount, active: this.activeFilter === 'all' }
    ];

    for (const group of this.results) {
      if (group.items.length > 0) {
        filters.push({
          key: group.module,
          label: group.moduleLabel,
          icon: group.moduleIcon,
          count: group.total,
          active: this.activeFilter === group.module
        });
      }
    }

    this.categoryFilters = filters;
  }

  /** Select a category filter */
  selectCategory(key: string): void {
    this.activeFilter = key;
    this.selectedIndex = -1;
    this.buildCategoryFilters();
    this.applyFilter();

    // A single-category view pages through that module in place (20 per page, on scroll).
    if (key === 'all') {
      this.moreSub?.unsubscribe();
      this.isLoadingMore = false;
    } else if (!this.modulePaging.has(key)) {
      this.loadModulePage(key, 1);
    }
    this.scrollOverlayToTop();
  }

  /** Cancel any in-flight page load and forget which pages were loaded (new/cleared query). */
  private resetModulePaging(): void {
    this.moreSub?.unsubscribe();
    this.isLoadingMore = false;
    this.modulePaging.clear();
  }

  /** Fetch one page of a single module and replace (page 1) or append (page 2+) it in the group. */
  private loadModulePage(module: string, page: number): void {
    const query = this.globalSearchService.normalizeQuery(this.searchControl.value);
    const group = this.results.find(g => g.module === module);
    if (!query || !group) return;

    this.moreSub?.unsubscribe();
    this.isLoadingMore = true;
    this.moreSub = this.globalSearchService.searchModule(module, query, page, MODULE_PAGE_SIZE).pipe(
      finalize(() => { this.isLoadingMore = false; })
    ).subscribe({
      next: result => {
        // Ignore a page for a query the user has already replaced or cleared.
        if (query !== this.globalSearchService.normalizeQuery(this.searchControl.value)) return;
        const known = new Set(page === 1 ? [] : group.items.map(i => i.id));
        group.items = page === 1 ? result.items : [...group.items, ...result.items.filter(i => !known.has(i.id))];
        group.hasMore = result.hasMore;
        this.modulePaging.set(module, { page: result.currentPage, hasMore: result.hasMore });
      },
      // Keep what is already shown; scrolling (or re-selecting the category) retries.
      error: () => {}
    });
  }

  /** Load the next page when the single-category list is scrolled near the bottom. */
  onOverlayScroll(event: Event): void {
    if (this.activeFilter === 'all' || this.isLoadingMore) return;
    const paging = this.modulePaging.get(this.activeFilter);
    if (!paging?.hasMore) return;

    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD_PX) {
      this.loadModulePage(this.activeFilter, paging.page + 1);
    }
  }

  private scrollOverlayToTop(): void {
    setTimeout(() => {
      const el = this.elementRef.nativeElement.querySelector('.search-results-overlay');
      if (el) el.scrollTop = 0;
    });
  }

  /** Apply current filter to results */
  private applyFilter(): void {
    if (this.activeFilter === 'all') {
      this.filteredResults = this.results;
    } else {
      this.filteredResults = this.results.filter(g => g.module === this.activeFilter);
    }
  }

  /** Escape and Ctrl+K / Cmd+K handler */
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showOverlay) {
      this.closeOverlay();
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.focusSearch();
    }
  }

  /**
   * Click outside closes the dropdown.
   *
   * Uses composedPath(), not `elementRef.nativeElement.contains(event.target)`.
   * Clicking a recent-search item sets the search control's value, which
   * synchronously re-renders the template and removes the (now-empty-input-only)
   * recent-items block — including the very element just clicked — before this
   * listener runs in the bubble phase. A removed node is never "contained" by
   * anything, so the old check always misread that as an outside click and
   * closed the overlay right after a recent search was applied (the search
   * kept running in the background and reopened it later with results, but
   * the popup visibly closed first). composedPath() reflects the DOM as it
   * was when the event was dispatched, before that re-render, so it stays
   * correct regardless of what the click handler went on to remove.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (!path.includes(this.elementRef.nativeElement)) {
      this.closeOverlay();
    }
  }

  /** Keyboard navigation inside the search input */
  onKeyDown(event: KeyboardEvent): void {
    const flatItems = this.getFlatItems();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = Math.min(this.selectedIndex + 1, flatItems.length - 1);
      this.scrollSelectedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
      this.scrollSelectedIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.selectedIndex >= 0 && this.selectedIndex < flatItems.length) {
        this.navigateToItem(flatItems[this.selectedIndex]);
      }
    }
  }

  /** Scroll the currently selected result item into view */
  private scrollSelectedIntoView(): void {
    setTimeout(() => {
      const el = this.elementRef.nativeElement.querySelector('.result-item.selected');
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  /** Focus the search input and show overlay */
  focusSearch(): void {
    this.searchInput?.nativeElement?.focus();
    this.showOverlay = true;
    this.overlayOpenedAt = Date.now();
    this.recentSearches = this.globalSearchService.getRecentSearches();
  }

  /** Close the dropdown overlay */
  closeOverlay(): void {
    this.showOverlay = false;
    this.selectedIndex = -1;
    this.searchInput?.nativeElement?.blur();
  }

  /**
   * Backdrop click — closes the overlay, except in the brief window right
   * after it opens. Reopening (showOverlay/recentSearches) is synchronous,
   * but the browser still needs a paint before the recent-items list is
   * actually hittable; a click landing in that gap (worse under load) hit
   * only the full-screen backdrop and silently closed the popup instead of
   * reaching the recent-search item the user meant to click. Ignoring the
   * backdrop's own close-click for a short grace period — not the real
   * outside-click/Escape paths — gives content time to paint first.
   */
  onBackdropClick(): void {
    if (Date.now() - this.overlayOpenedAt < 250) {
      return;
    }
    this.closeOverlay();
  }

  /** Show overlay on input focus */
  onInputFocus(): void {
    this.showOverlay = true;
    this.overlayOpenedAt = Date.now();
    this.recentSearches = this.globalSearchService.getRecentSearches();
  }

  /** Navigate to a search result item */
  navigateToItem(item: GlobalSearchItem): void {
    // A Document whose parent record has no known route cannot be opened.
    // Reminder is exempt from this check — it never navigates at all (see
    // below), so it has no real route to require. Milestone is exempt for
    // the same reason as Reminder: it has no synchronous fallback route at
    // all (no standalone page, no listing page) — its real destination is
    // always resolved asynchronously below, and a failed resolution is
    // handled there with a toast rather than silently no-op'ing here.
    if (!item.route && item.module !== 'reminder' && item.module !== 'milestone') return;
    const query = this.searchControl.value;
    if (query) {
      this.globalSearchService.saveRecentSearch(query);
      this.globalSearchService.trackSelection(query, item);
    }
    this.closeOverlay();
    this.searchControl.setValue('', { emitEvent: false });
    this.latestQuery = '';
    this.results = [];
    this.filteredResults = [];
    this.hasSearched = false;

    // Reminder has no dedicated page to land on — open the existing
    // AddReminderModalComponent (the same dialog RemindersPanelComponent's
    // own onEditReminder() already opens for a real edit click) directly,
    // right where the user already is. No navigation, no query param.
    if (item.module === 'reminder') {
      this.openReminderFromSearch(item.id);
      return;
    }

    // A Document attached to a Task/Ticket/Note: resolve which bigger record
    // (Job/Bid/Customer/...) it really belongs to before navigating, falling
    // back to item.route if that lookup fails or finds nothing better.
    if (item.grandparentType && item.grandparentId != null) {
      this.globalSearchService.resolveGrandparentRoute(item.grandparentType, item.grandparentId)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }

    // Task/Ticket/Note: land on the record's actual parent (Job/Bid/Customer)
    // with that parent's own Tasks/Tickets/Notes tab selected AND the
    // record's popup auto-opened on top — instead of the generic
    // /tasks|tickets|notes?openId= listing page. Falls back to item.route
    // for any parent type not yet supported, or on error (see
    // resolveTaskParentSidebarRoute() / ...Ticket... / ...Note... for the
    // exact scope and tab indices).
    if (item.module === 'task') {
      this.globalSearchService.resolveTaskParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    if (item.module === 'ticket') {
      this.globalSearchService.resolveTicketParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    if (item.module === 'note') {
      this.globalSearchService.resolveNoteParentSidebarRoute(item.id)
        .subscribe(resolved => this.router.navigateByUrl(resolved ?? item.route!));
      return;
    }
    // Milestone: land on its real parent's Summary tab (see
    // moduleIdToMilestoneSidebarRoute's docblock for why Summary, not a
    // "Milestones" tab — none exists yet on any parent detail page). Unlike
    // Task/Ticket/Note there is no generic listing-page fallback route to
    // fall back to, so a failed resolution surfaces a toast instead of
    // navigating to an empty/wrong URL.
    if (item.module === 'milestone') {
      this.globalSearchService.resolveMilestoneParentSidebarRoute(item.id).subscribe(resolved => {
        if (resolved) {
          this.router.navigateByUrl(resolved);
        } else {
          this.toasterService.showErrorMessage('This milestone\'s record could not be opened.');
        }
      });
      return;
    }
    this.router.navigateByUrl(item.route!);
  }

  /**
   * RemindersModule (which declares AddReminderModalComponent,
   * standalone: false) is only ever eagerly reachable via SharedModule,
   * which several FEATURE modules import — it isn't guaranteed to already
   * be loaded on every page (e.g. a user on the Dashboard who has never
   * opened Task/Ticket/Proposal yet). Mirrors the exact fix already
   * applied for the same class of problem with Task's
   * ensureTaskModuleLoaded() (job-detail.component.ts) — importing the
   * module ourselves is enough to run its @NgModule decorator regardless
   * of whether Router has "officially" loaded it, and resolves instantly
   * from the module cache if it's already loaded.
   */
  private remindersModuleLoaded = false;
  private async ensureRemindersModuleLoaded(): Promise<void> {
    if (this.remindersModuleLoaded) {
      return;
    }
    await import('../../reminders/reminders.module');
    this.remindersModuleLoaded = true;
  }

  /**
   * Reminder search result click: fetch the exact reminder (the existing
   * GET /reminders/{id} endpoint already independently verifies the user
   * is allowed to see it) and open it in the existing edit dialog —
   * mirrors RemindersPanelComponent.onEditReminder() exactly. Handles a
   * missing/deleted/unauthorized reminder gracefully via a toast.
   */
  private openReminderFromSearch(id: number | string): void {
    this.remindersService.fetchReminderById(id).subscribe({
      next: async (response: any) => {
        const reminder = response?.data;
        if (!reminder) {
          this.toasterService.showErrorMessage('Reminder not found.');
          return;
        }
        await this.ensureRemindersModuleLoaded();
        this.dialog.open(AddReminderModalComponent, {
          panelClass: 'reminder-modal-panel',
          disableClose: false,
          autoFocus: true,
          data: { reminder },
        });
      },
      error: () => {
        this.toasterService.showErrorMessage(
          'This reminder could not be found or you no longer have access to it.'
        );
      },
    });
  }

  /** "View all": stay in the dropdown, show only this module and load the rest of it on scroll. */
  viewAllResults(moduleKey: string): void {
    if (!this.results.some(g => g.module === moduleKey)) return;
    const query = this.globalSearchService.normalizeQuery(this.searchControl.value);
    if (query) {
      this.globalSearchService.saveRecentSearch(query);
    }
    this.selectCategory(moduleKey);
  }

  /** Apply a recent search term */
  applyRecentSearch(term: string): void {
    this.searchControl.setValue(term);
  }

  /** Remove a single recent search term */
  removeRecentSearch(term: string, event: Event): void {
    event.stopPropagation();
    this.globalSearchService.removeRecentSearch(term);
    this.recentSearches = this.recentSearches.filter(r => r !== term);
  }

  /** Clear recent searches */
  clearRecentSearches(): void {
    this.globalSearchService.clearRecentSearches();
    this.recentSearches = [];
  }

  /** Open Google search for the current query in a new tab */
  askGoogle(): void {
    const query = this.searchControl.value?.trim();
    if (!query) return;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener');
  }

  /** Open ChatGPT with the current query in a new tab */
  askChatGPT(): void {
    const query = this.searchControl.value?.trim();
    if (!query) return;
    window.open(`https://chatgpt.com/?q=${encodeURIComponent(query)}`, '_blank', 'noopener');
  }

  /** Clear the search input */
  clearSearch(): void {
    this.searchControl.setValue('');
    this.resetModulePaging();
    this.errorMessage = null;
    this.results = [];
    this.filteredResults = [];
    this.hasSearched = false;
    this.selectedIndex = -1;
    this.searchInput?.nativeElement?.focus();
  }

  /** Flatten all result items for keyboard navigation (uses filteredResults) */
  private getFlatItems(): GlobalSearchItem[] {
    return this.filteredResults.reduce<GlobalSearchItem[]>((acc, group) => {
      return acc.concat(group.items);
    }, []);
  }

  /** Check if a flat-index item is selected (for highlight) */
  isItemSelected(group: GlobalSearchModuleResult, itemIndex: number): boolean {
    let flatIndex = 0;
    for (const g of this.filteredResults) {
      if (g === group) {
        return (flatIndex + itemIndex) === this.selectedIndex;
      }
      flatIndex += g.items.length;
    }
    return false;
  }
}
